import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { AuthenticationApiService, UsersApiService } from '@portal-front-end/api';
import { AuthenticationType, AuthErrorResponse, User } from '@portal-front-end/models';
import { from, Observable, of } from 'rxjs';
import {
    catchError, map,
    switchMap,
    tap
} from 'rxjs/operators';
import { MessageNotifBannerService, MessageType } from '../../shared/message-notif-banner';
import { RolesPermissionStore } from '../roles-permissions-store';
import { AuthCookieService } from './auth-cookie.service';
import {
    authForgotPasswordError, authForgotPasswordRequest,
    authForgotPasswordSuccess, authLoadUser,
    authLoadUserError,
    authLoadUserType, authLoggedOut, authLogin,
    authLoginError,
    authLogout,
    authResetPassword,
    authResetPasswordError, authResetPasswordSuccess, authRouteToPublicType, authUserLoaded
} from './auth-store.actions';

@Injectable({ providedIn: 'root' })
export class AuthStoreEffects {

    constructor(
        private actions$: Actions,
        private cookies: AuthCookieService,
        private authApi: AuthenticationApiService,
        private rolesPermissionsStore: RolesPermissionStore,
        private authUser: UsersApiService,
        private router: Router,
        private notifBanner: MessageNotifBannerService,
    ) { }

    login$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(authLogin),
            switchMap((action) =>
                this.authApi.login(action.payload).pipe(
                    map((auth: AuthenticationType) => {
                        this.cookies.storeAuth(auth);
                        this.cookies.storeAccessToken(auth.access_token);
                        return authLoadUser({ isFromLoginRequest: true });
                    }),
                    catchError(err => of(authLoginError(err)))
                )
            ),
        )
    );

    loginError = createEffect(() =>
        this.actions$.pipe(
            ofType(authLoginError),
            map((err) => err.error),
            tap((err: AuthErrorResponse) => {
                if (err.error === 'invalid_grant') {
                    this.notifBanner.flagMessage({ type: MessageType.error, message: 'Invalid username or password' });
                } else {
                    this.notifBanner.flagMessage({ type: MessageType.error, message: 'An error has occured, contact administrator' });
                }
            })
        ),
        { dispatch: false }
    );

    loadUser$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(authLoadUserType),
            switchMap((payload: { isFromLoginRequest: boolean }) =>
                this.authUser.getLoggedInUserDetails().pipe(
                    map((user) => {
                        return authUserLoaded({ user, isFromLogin: payload.isFromLoginRequest });
                    }),
                    catchError(err =>
                        from([authLogout(), authLoadUserError({ error: err })])
                    )
                )
            ),
        )
    );

    userLoaded$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authUserLoaded),
            tap((payload: { user: User, isFromLogin: boolean }) => {
                const orgs = payload.user.organisations;
                if (orgs.length > 0) {
                    if (payload.isFromLogin) {
                        let primaryOrg = orgs.find(org => org.primary) || orgs[0];
                        this.rolesPermissionsStore.loadPermissions(primaryOrg.organisation.organisationId);
                        this.router.navigate(['/portal', primaryOrg.organisation.shortCode]);
                    }
                }
            }),
        ),
        { dispatch: false }
    );

    loadUserError$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(authLoadUserError),
            tap(() => this.notifBanner.flagMessage({ type: MessageType.error, message: 'An error has occured, contact administrator' }))
        )
        , { dispatch: false }
    );


    logout$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(authLogout),
            map(() => {
                this.cookies.deleteSession();
                this.router.navigate(['/']);
                return authLoggedOut();
            }),
        )
    );

    routeToPublic$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(authRouteToPublicType),
            map(() => authLoggedOut()),
            tap(() => this.router.navigate(['/'])),
        )
    );

    forgotPasswordRequest$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(authForgotPasswordRequest),
            switchMap((action) =>
                this.authApi.forgotPasswordRequest(action.payload).pipe(
                    map(() => {
                        return authForgotPasswordSuccess();
                    }),
                    catchError(err => of(authForgotPasswordError(err)))
                )
            ),
        )
    );

    forgotPasswordSuccess = createEffect(() =>
        this.actions$.pipe(
            ofType(authForgotPasswordSuccess),
            tap(() => this.notifBanner.flagMessage({ type: MessageType.success, message: 'Successfully sent an link.' }))
        ),
        { dispatch: false }
    );

    forgotPasswordError = createEffect(() =>
        this.actions$.pipe(
            ofType(authForgotPasswordError),
            tap((err: any) => {
                if(err.error?.message != undefined){
                    this.notifBanner.flagMessage({ type: MessageType.error, message: err.error.message });
                } else {
                    this.notifBanner.flagMessage({ type: MessageType.error, message: 'An error has occured, contact administrator' });
                }
               
            })
        ),
        { dispatch: false }
    );

    resetPasswordRequest$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(authResetPassword),
            switchMap((action) =>
                this.authApi.resetPassword(action.payload).pipe(
                    map(() => {
                        this.router.navigate(['/']);
                        return authResetPasswordSuccess();
                    }),
                    catchError(err => of(authResetPasswordError(err)))
                )
            ),
        )
    );

    resetPasswordSuccess = createEffect(() =>
        this.actions$.pipe(
            ofType(authResetPasswordSuccess),
            tap(() => this.notifBanner.flagMessage({ type: MessageType.success, message: 'Successfully reset your password.' }))
        ),
        { dispatch: false }
    );

    resetPasswordError = createEffect(() =>
        this.actions$.pipe(
            ofType(authResetPasswordError),
            tap(() => this.notifBanner.flagMessage({ type: MessageType.error, message: 'An error has occured, contact administrator' }))
        ),
        { dispatch: false }
    );

}
